import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { hashHistory } from 'react-router';

import { BackLink } from '../../components/atoms/BackLink';
import { ErrorFallback } from '../../components/layouts/ErrorFallback';
import { Hidden } from '../../components/layouts/Hidden';
import { LoadingSpinner } from '../../components/layouts/LoadingSpinner';
import { Page } from '../../components/layouts/Page';
import { CheckoutButton } from '../../components/molecules/CheckoutButton';
import { Basket } from '../../components/organisms/Basket/Basket';
import { Reorder } from '../../components/organisms/Reorder';
import { ReorderList } from '../../components/organisms/Reorder/ReorderList';
import { CampaignConsentDialog } from '../../components/partials/CampaignConsentDialog';
import { Menu } from '../../components/partials/Menu';
import { PageHeader } from '../../components/partials/PageHeader';
import ProfilePanel from '../../components/partials/ProfilePanel';
import { Currency } from '../../core/api/general/general.types';
import { MenuItem } from '../../core/api/menu/menu.types';
import { GetReordersQueryParams } from '../../core/api/order/order.types';
import { DeliveryType } from '../../core/api/restaurant/restaurant.types';
import { useCrossSellMenuItems } from '../../core/hooks/fetch/useCrossSellMenuItems';
import { useMenu } from '../../core/hooks/fetch/useMenu';
import { useNutritionInfo } from '../../core/hooks/fetch/useNutritionInfo';
import { useOrderHistory } from '../../core/hooks/fetch/useOrderHistory';
import { useReorders } from '../../core/hooks/fetch/useReorders';
import { useRestaurantDetails } from '../../core/hooks/fetch/useRestaurantDetails';
import { useRestaurantSummaries } from '../../core/hooks/fetch/useRestaurantSummaries';
import { useBasket } from '../../core/hooks/shared/useBasket';
import { useReorderList } from '../../core/hooks/shared/useReorderList';
import { analyticsService } from '../../core/services/analyticsService';
import { flagService } from '../../core/services/flagService';
import { BasketSource } from '../../core/types/basket.types';
import { ClickItemPayloadType } from '../../core/types/menu.types';
import { RouteParams } from '../../core/types/route.types';
import { formatNumberToCurrency } from '../../utils/basketUtils';
import { domUtils } from '../../utils/domUtils';
import { getUtils } from '../../utils/getUtils';
import { replaceCollectionWithPickup } from '../../utils/localisationUtils';
import {
  checkMenuItemHasAllergens,
  getViewportTopOffset,
} from '../../utils/menuUtils';
import { urlUtils } from '../../utils/urlUtils';
import { Track } from '../../weborder/analytics';
import {
  DispatchProps,
  StateProps,
} from '../../weborder/component/Restaurant/index';
import { isPhotonV2 } from '../../weborder/Utilities';
import { CrossSellDialog } from './components/CrossSellDialog';
import { MenuOptionsDialog } from './components/MenuOptionsDialog';
import { useMenuItemNutritionInfo } from './hooks/useMenuItemNutritionInfo';
import { MenuDetailDialog } from './partials/MenuDetailDialog';
import { NotAvailableWarning } from './partials/NotAvailableWarning';
import { StoreHeader } from './partials/StoreHeader';
import { getMenuItemById } from './utils/getMenuItemById';
import {
  checkIsCollectionRestaurantOnly,
  checkIsTableServiceOnly,
} from './utils/locationHeaderUtils';
import {
  checkIsClosedAndDisplayAlert,
  checkIsOutsideDeliveryZone,
  getButtonState,
} from './utils/restaurantMenuUtils';

type Props = StateProps & DispatchProps & RouteParams;

const BASKET_HEADER_AND_BUTTON = 150;
const BASKET_TOP_MARGIN = 30;
export const NAV_BAR_HEIGHT = 50;

const useStyles = (viewportTopOffset) =>
  makeStyles((theme: Theme) => {
    const maxHeightOffset =
      viewportTopOffset + BASKET_HEADER_AND_BUTTON + NAV_BAR_HEIGHT;
    return {
      reorder: {
        marginBottom: theme.spacing(2),
      },

      [theme.breakpoints.up('lg')]: {
        floatingBasket: {
          maxHeight: `calc(100vh - ${viewportTopOffset}px)`,
          overflow: 'hidden',
          position: 'sticky',
          top: viewportTopOffset,

          '&.hasNavigation': {
            top: `${viewportTopOffset + NAV_BAR_HEIGHT}px`,

            '& .basketItems': {
              maxHeight: `calc(100vh - ${maxHeightOffset}px)`,
              overflowY: 'auto',
            },
          },

          '& .basketOutline': {
            border: `1px solid ${theme.palette.grey[400]}`,
            borderBottom: 'none',
          },

          '& .basketItems': {
            maxHeight: `calc(100vh - ${
              BASKET_HEADER_AND_BUTTON + viewportTopOffset
            }px)`,
            overflowY: 'auto',
          },
        },
      },
    };
  })();

export function RestaurantMenu(props: Props): JSX.Element {
  const {
    actions,
    AddItemToBasket: addItemToBasket,
    AddToExpandedState: addToExpandedState,
    appConfig,
    basket,
    collectionRestaurants,
    collectionSummaries,
    customer,
    deliveryLocation,
    deliveryRestaurants,
    deliverySummaries,
    EditTableNumber: editTableNumber,
    expandedState,
    InitializeExpandedState: initializeExpandedState,
    isLoggedIn,
    languages,
    menus,
    nutritionInfos,
    params,
    previousOrders,
    PushLoginUrl: pushLoginUrl,
    RemoveFromExpandedState: removeFromExpandedState,
    RemoveItemFromBasket: removeItemFromBasket,
    restaurantCollectionReorders,
    restaurantDeliveryReorders,
    SetRestaurantUrl: setRestaurantUrl,
    setTableLock,
    translate,
    urlLocation,
    cookieConsentSelected,
  } = props;

  const languageCode = getUtils.activeLanguageCode(languages);

  const isReorderEnabled = flagService.isFlagOn('reorderMenu');
  const isReorderUseNewEndpointEnabled = flagService.isFlagOn(
    'reorderUseNewEndpoint',
  );
  const isCrossSellMenuDialogEnabled = flagService.isFlagOn(
    'crossSellMenuDialog',
  );

  const isOldReorderEnabled =
    isReorderEnabled && !isReorderUseNewEndpointEnabled && isLoggedIn;
  const isNewReorderEnabled =
    isReorderEnabled && isReorderUseNewEndpointEnabled && isLoggedIn;

  const { type } = params;
  const concessionStoreId = Number(params.concessionStoreId) || undefined;
  const id = Number(params.id);
  const vrId = Number(params.vrid);

  const [showMenuDetailDialog, setShowMenuDetailDialog] =
    useState<boolean>(false);
  const [showMenuOptionsDialog, setShowMenuOptionsDialog] =
    useState<boolean>(false);
  const [showCrossSellDialog, setShowCrossSellDialog] =
    useState<boolean>(false);
  const [showMenuSearch, setShowMenuSearch] = useState<boolean>(false);

  const [selectedMenuItem, setSelectedMenuOption] = useState<MenuItem>(null);
  const [hasLockedTableNumber, setHasLockedTableNumber] =
    useState<boolean>(false);

  const {
    collectionData: collectionSummariesData,
    isLoading: isRestaurantSummariesLoading,
  } = useRestaurantSummaries(
    appConfig,
    collectionSummaries,
    deliverySummaries,
    actions.getRestaurantSummaries,
  );

  const {
    data: restaurantDetailData,
    isError: isRestaurantDetailError,
    isLoading: isRestaurantDetailLoading,
    isSuccess: isRestaurantDetailSuccess,
  } = useRestaurantDetails(
    actions.loadCollectionLocationAreas,
    actions.loadRestaurantDetail,
    collectionRestaurants,
    deliveryRestaurants,
    params,
  );

  const currency = restaurantDetailData?.Currency || Currency.EUR;

  const {
    data: menuData,
    isError: isMenuError,
    isLoading: isMenuLoading,
    isSuccess: isMenuSuccess,
  } = useMenu(actions.loadMenu, menus, restaurantDetailData?.MenuUrl);

  const {
    data: nutritionInfoData,
    isError: isNutritionInfoError,
    isLoading: isNutritionInfoLoading,
    showData: showNutritionInfo,
  } = useNutritionInfo(
    actions.loadNutritionInfo,
    nutritionInfos,
    restaurantDetailData,
  );

  const { menuItemNutritionInfo, menuItemOptionSetItemNutritionInfos } =
    useMenuItemNutritionInfo(nutritionInfoData, selectedMenuItem);

  const {
    data: orderHistoryData,
    isEmpty: isOrderHistoryEmpty,
    isError: isOrderHistoryError,
    isLoading: isOrderHistoryLoading,
    isSuccess: isOrderHistorySuccess,
  } = useOrderHistory(
    isOldReorderEnabled,
    actions.loadOrderHistory,
    previousOrders,
  );

  const { reorderList, showReorder } = useReorderList(
    restaurantDetailData?.DeliveryType,
    isOrderHistoryEmpty,
    isOrderHistoryError,
    isOrderHistoryLoading,
    isOrderHistorySuccess,
    orderHistoryData,
    restaurantDetailData?.PhysicalRestaurantId,
  );

  const restuarantReordersQueryParam: GetReordersQueryParams =
    restaurantDetailData?.PhysicalRestaurantId
      ? {
          fulfilmentType: restaurantDetailData?.DeliveryType,
          storeIds: [restaurantDetailData?.PhysicalRestaurantId],
        }
      : {};

  const isDeliveryOrder =
    restaurantDetailData?.DeliveryType === DeliveryType.Delivery;

  const reorderType = isDeliveryOrder
    ? 'restaurantDeliveryReorders'
    : 'restaurantCollectionReorders';

  const reordersDataDictionary = isDeliveryOrder
    ? restaurantDeliveryReorders
    : restaurantCollectionReorders;

  const {
    data: reorderData,
    isLoading: isReorderLoading,
    showData: showReorderList,
  } = useReorders(
    actions.loadReorders,
    reorderType,
    reordersDataDictionary,
    restuarantReordersQueryParam,
    isNewReorderEnabled,
  );

  // #region  TODO - ORD-59 put hasMenuConcessions, handleClickItem and other menu related data/handlers into a hook useMenuData
  const hasMenuConcessions: boolean =
    isMenuSuccess && menuData?.ConcessionStores.length > 0;

  const trackMenuItemSelected = (item: MenuItem): void => {
    const hasAllergenInfo = checkMenuItemHasAllergens(
      nutritionInfoData,
      item.PublicId,
    );
    const eventData = {
      amp_descriptionLength: item.Description?.length || 0,
      amp_hasAllergenInfo: hasAllergenInfo,
      amp_hasDescription: !!item.Description,
      amp_hasImage: !!item.ImageUrl,
      amp_hasModifiers: !!item.MenuItemOptionSets.length,
      amp_itemName: item.Name,
      amp_itemPrice: item.Price,
      amp_label: 'Menu Item Selected',
      amp_productId: item.MenuItemId.toString(),
    };
    analyticsService.track(eventData);
  };

  const handleClickItem = useCallback(
    (payload: ClickItemPayloadType) => {
      if (!isRestaurantDetailSuccess) {
        console.error('Cannot click - restaurant is', 'none', props);
        return;
      }

      if (checkIsClosedAndDisplayAlert(restaurantDetailData, translate)) {
        return;
      }

      const { item } = payload;
      if (hasMenuConcessions) {
        const concessionItem = Object.assign(item, {
          ConcessionStoreId: concessionStoreId,
        });
        setSelectedMenuOption(concessionItem);
      }
      setSelectedMenuOption(item);

      const hasMenuItemOptionSets = item.MenuItemOptionSets.length > 0;
      if (hasMenuItemOptionSets) {
        trackMenuItemSelected(item);
        setShowMenuOptionsDialog(true);
      } else {
        trackMenuItemSelected(item);
        setShowMenuDetailDialog(true);
      }
    },
    [concessionStoreId, hasMenuConcessions, isRestaurantDetailSuccess],
  );

  const {
    handleAddItem: handleAddItemToBasket,
    handleRemoveItem: handleRemoveItemFromBasket,
    isTotalOverMinOrder,
    itemCount: basketItemCount,
    items: basketItems,
    localisedTotal: localisedBasketTotal,
    summarisedItems: summarisedBasketItems,
    total: basketTotal,
  } = useBasket(
    basket,
    concessionStoreId,
    languageCode,
    nutritionInfoData,
    addItemToBasket,
    removeItemFromBasket,
    restaurantDetailData,
    translate,
    handleClickItem,
  );

  const {
    data: crossSellMenuItemsData,
    getData: getCrossSellItems,
    isLoading: isCrossSellMenuItemsLoading,
  } = useCrossSellMenuItems({
    basketItems: isCrossSellMenuDialogEnabled ? basket?.items : [],
    isAutoFetchEnabled: false,
    menu: menuData,
    orderTotal: basketTotal,
  });

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('sm'));

  const viewportTopOffset = getViewportTopOffset(isMobileScreen);
  const heightOfConcessionNav = 65;
  const offsetForConcession = hasMenuConcessions ? heightOfConcessionNav : 0;
  const offsetTop = viewportTopOffset + BASKET_TOP_MARGIN + offsetForConcession;

  const classes = useStyles(offsetTop);

  const concessionStoreIdPath = concessionStoreId
    ? `/${concessionStoreId}`
    : '';

  useEffect(() => {
    if (isRestaurantDetailSuccess) {
      domUtils.skipToContent();
    }
  }, [isRestaurantDetailSuccess]);

  useEffect(() => {
    setRestaurantUrl();
  }, []);

  useEffect(() => {
    const setLocationNumberFromUrl = (): void => {
      const urlLocationQuery = new URLSearchParams(urlLocation.search).get(
        'location_id',
      );
      const locationId = parseInt(urlLocationQuery);

      if (Number.isInteger(locationId)) {
        editTableNumber(vrId, locationId);
        setHasLockedTableNumber(true);
      }
    };

    setLocationNumberFromUrl();
  }, []);

  useEffect(() => {
    if (isRestaurantDetailSuccess) {
      const trackUserProperties = (): void => {
        const userId = customer.status === 'success' ? customer.data.UserId : 0;
        const whiteLabelId = appConfig.WhiteLabelId;

        const eventData = {
          amp_userProperties: {
            channel: 'Web',
            PRID: id,
            userId,
            WLID: whiteLabelId,
          },
        };
        analyticsService.track(eventData);
      };

      trackUserProperties();
    }
  }, [customer.status, isRestaurantDetailSuccess]);

  useEffect(() => {
    const menuItemId = new URLSearchParams(urlLocation.search).get('item');

    if (cookieConsentSelected && menuItemId && menuData) {
      const menuItem = getMenuItemById({
        menuItemId,
        menuSections: menuData.MenuSections,
      });

      if (menuItem) {
        hashHistory.replace(hashHistory.getCurrentLocation()?.pathname);
        handleClickItem({ menuId: menuData?.MenuId, item: menuItem });
      }
    }
  }, [
    cookieConsentSelected,
    handleClickItem,
    menuData,
    menuData?.MenuId,
    menuData?.MenuSections,
    urlLocation.search,
  ]);

  const navigateToCheckout = (): void => {
    const eventData = {
      amp_basketValue: basketTotal,
      amp_isLoggedin: customer.status === 'success',
      amp_label: 'Checkout Selected',
      amp_numItemsInBasket: basketItems.length,
    };
    analyticsService.track(eventData);

    if (isRestaurantDetailSuccess && customer.status === 'success') {
      Track(
        'Orders',
        'Checkout',
        {
          event_label: restaurantDetailData.RestaurantName,
          label: 'Checkout',
          section: 'Orders',
        },
        'InitiateCheckout',
      );

      const nextUrl = `/checkout/${vrId}/${type}/${id}${concessionStoreIdPath}`;
      urlUtils.navigateToHash(nextUrl);
    } else {
      loginAndNavigateTo();
    }
  };

  const handleOnCheckout = async (): Promise<void> => {
    const crossSellItems = isCrossSellMenuDialogEnabled
      ? await getCrossSellItems()
      : [];

    const isCrossSellItemsAvailable = crossSellItems.length > 0;
    isCrossSellItemsAvailable
      ? setShowCrossSellDialog(true)
      : navigateToCheckout();
  };

  const checkoutButton = useMemo(() => {
    if (
      basketItems.length === 0 ||
      !isRestaurantDetailSuccess ||
      !isMenuSuccess
    ) {
      return null;
    }

    const isOutsideDeliveryZone =
      checkIsOutsideDeliveryZone(restaurantDetailData);
    const { buttonLabel, isCheckoutEnabled } = getButtonState(
      restaurantDetailData.AcceptPreOrders,
      isDeliveryOrder,
      isOutsideDeliveryZone,
      restaurantDetailData.IsOpen,
      translate,
    );

    const localisedMinOrder = formatNumberToCurrency(
      restaurantDetailData.MinimumOrder,
      currency,
      languageCode,
    );

    const isDisabled =
      isCrossSellMenuItemsLoading ||
      (!isTotalOverMinOrder && !isCheckoutEnabled);

    return (
      <CheckoutButton
        basketItemCount={basketItemCount}
        buttonLabel={buttonLabel}
        isDisabled={isDisabled}
        isLoading={isCrossSellMenuItemsLoading}
        isTotalOverMinOrder={isTotalOverMinOrder}
        localisedBasketTotal={localisedBasketTotal}
        localisedMinOrder={localisedMinOrder}
        onCheckout={handleOnCheckout}
        translate={translate}
      />
    );
  }, [
    basketItems,
    customer.status,
    isCrossSellMenuItemsLoading,
    isMenuSuccess,
    isRestaurantDetailSuccess,
  ]);

  if (isRestaurantDetailError || isMenuError) {
    return <ErrorFallback logIdentifier="restaurant-menu" />;
  }

  if (isRestaurantSummariesLoading || isRestaurantDetailLoading) {
    return <LoadingSpinner />;
  }

  const renderOrderBasket = () => {
    if (basketItems.length === 0 || !isMenuSuccess) {
      return null;
    }

    const isOutsideDeliveryZone =
      checkIsOutsideDeliveryZone(restaurantDetailData);
    const { buttonLabel, isCheckoutEnabled } = getButtonState(
      restaurantDetailData.AcceptPreOrders,
      isDeliveryOrder,
      isOutsideDeliveryZone,
      restaurantDetailData.IsOpen,
      translate,
    );

    const localisedMinOrder = formatNumberToCurrency(
      restaurantDetailData.MinimumOrder,
      currency,
      languageCode,
    );

    const isCheckoutDisabled =
      isCrossSellMenuItemsLoading ||
      (!isTotalOverMinOrder && !isCheckoutEnabled);

    const floatingBasketStyles = clsx(
      classes.floatingBasket,
      !!menuData?.DisplaySectionLinks && 'hasNavigation',
    );

    return (
      <Basket
        basketItemClassName="basketItems"
        basketItemCount={basketItemCount}
        buttonLabel={buttonLabel}
        className={floatingBasketStyles}
        concessionStores={menuData?.ConcessionStores}
        currency={restaurantDetailData.Currency}
        isCheckoutButtonLoading={isCrossSellMenuItemsLoading}
        isCheckoutDisabled={isCheckoutDisabled}
        isTotalOverMinOrder={isTotalOverMinOrder}
        languageCode={languageCode}
        localisedBasketTotal={localisedBasketTotal}
        localisedMinOrder={localisedMinOrder}
        onAddItem={handleAddItemToBasket}
        onCheckout={handleOnCheckout}
        onRemoveItem={handleRemoveItemFromBasket}
        source={BasketSource.MenuBasket}
        summarisedBasketItems={summarisedBasketItems}
        translate={translate}
      />
    );
  };

  const loginAndNavigateTo = () => {
    const locationHash = urlUtils.getWindowLocation('hash');
    let loginUrl = locationHash.substring(1);

    loginUrl = loginUrl.replace('restaurant', 'checkout');
    pushLoginUrl(loginUrl);

    const redirectUrl = type === 'delivery' ? `/phone/${id}` : 'phone';
    urlUtils.navigateToHash(redirectUrl);
  };

  const getBacklinkUrl = (): string => {
    const {
      DisplayPickupRestaurantListScreen: displayPickupRestaurantListScreen,
      DisplayDeliveryRestaurantListScreen: displayDeliveryRestaurantListScreen,
    } = appConfig;

    const isCollection = type === 'collection';
    const isDelivery = type === 'delivery';

    const skipStoreCollectionList =
      isCollection && !displayPickupRestaurantListScreen;
    const skipStoreDeliveryList =
      isDelivery && !displayDeliveryRestaurantListScreen;

    if (isPhotonV2()) {
      return '';
    }

    if (skipStoreCollectionList) {
      return '/where';
    }

    if (skipStoreDeliveryList) {
      return '/delivery';
    }

    if (isCollection) {
      return '/collection';
    }

    const deliveryLocationId = params?.id;
    if (isDelivery && deliveryLocationId) {
      return `/delivery/${deliveryLocationId}`;
    }

    return '/delivery';
  };

  const backlinkUrl = getBacklinkUrl();

  const handleBackClick = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Restaurant Menu',
    };
    analyticsService.track(eventData);
    if (isPhotonV2()) {
      const { origin } = window.location;
      if (
        origin.includes('my.flipdish.com') ||
        origin.includes('my.flipdishdev.com')
      ) {
        urlUtils.navigateToUrl(
          `${window.location.origin}/${appConfig.AppNameId}`,
        );
      } else {
        urlUtils.navigateToUrl(origin);
      }
    }
  };

  const isOutsideDeliveryZone =
    checkIsOutsideDeliveryZone(restaurantDetailData);

  const isTableServiceOnly = !isDeliveryOrder
    ? checkIsTableServiceOnly(restaurantDetailData)
    : false;

  const isCollectionRestaurantOnly = !isDeliveryOrder
    ? checkIsCollectionRestaurantOnly(appConfig, collectionSummariesData)
    : false;

  const showBackLinkButton = !isTableServiceOnly && !isCollectionRestaurantOnly;
  const showOldReorderComponent = isOldReorderEnabled && showReorder;

  const basketStyles = clsx('fd-content__side', classes.basket);

  const reorderListTitle = isDeliveryOrder
    ? (translate('ReorderCard_RecentDeliveryOrders') as string)
    : replaceCollectionWithPickup(
        translate('ReorderCard_RecentCollectionOrders') as string,
        appConfig.CountryCode,
      );

  return (
    <>
      <Page pageName="restaurant-menu">
        <PageHeader
          params={{
            id: id?.toString(),
            type,
            vrid: vrId.toString(),
          }}
          showProfileMenu
          title={restaurantDetailData.RestaurantName}
          titleTestId="restaurant-menu-title"
        >
          {showBackLinkButton && (
            <BackLink
              label={translate('Back')}
              linkPath={backlinkUrl}
              onClick={handleBackClick}
              testId="restaurant-back"
            />
          )}
        </PageHeader>

        <article className="fd-content__main">
          <StoreHeader
            currency={currency}
            deliveryLocation={deliveryLocation}
            hasLockedTableNumber={hasLockedTableNumber}
            languageCode={languageCode}
            prId={id}
            restaurant={restaurantDetailData}
            setTableLock={setTableLock}
            tableNumber={basket.tableNumber}
            translate={translate}
            type={type}
            vrId={vrId}
          />
          <NotAvailableWarning
            isDeliveryOrder={isDeliveryOrder}
            isOutsideDeliveryZone={isOutsideDeliveryZone}
            restaurantData={restaurantDetailData}
            translate={translate}
          />

          {showOldReorderComponent && (
            <div className={classes.reorder}>
              <Reorder
                deliveryLocationId={params?.id}
                isLoading={isOrderHistoryLoading}
                languageCode={languageCode}
                listTitle={
                  isDeliveryOrder
                    ? (translate('ReorderCard_RecentDeliveryOrders') as string)
                    : replaceCollectionWithPickup(
                        translate(
                          'ReorderCard_RecentCollectionOrders',
                        ) as string,
                        appConfig.CountryCode,
                      )
                }
                menu={menuData}
                orders={reorderList}
                setBasket={actions.setBasket}
                setReorderOrderId={actions.setReorderOrderId}
                source="Menu"
                translate={translate}
              />
            </div>
          )}

          {showReorderList && (
            <div className={classes.reorder}>
              <ReorderList
                deliveryLocationId={params?.id}
                isLoading={isReorderLoading}
                languageCode={languageCode}
                listTitle={reorderListTitle}
                orders={reorderData}
                setBasket={actions.setBasket}
                setUnavailableReorderItems={actions.setUnavailableReorderItems}
                source="Store list"
                translate={translate}
              />
            </div>
          )}

          <Menu
            basket={basket}
            checkoutButton={checkoutButton}
            configData={appConfig}
            currency={currency}
            expandedState={expandedState}
            isLoading={isMenuLoading || isNutritionInfoLoading}
            languageCode={languageCode}
            menuData={menuData}
            onAddExpanded={addToExpandedState}
            onAddItem={handleAddItemToBasket}
            onInitExpanded={initializeExpandedState}
            onItemClick={handleClickItem}
            onRemoveExpanded={removeFromExpandedState}
            onRemoveItem={handleRemoveItemFromBasket}
            params={params}
            restaurantData={restaurantDetailData}
            setShowMenuSearch={setShowMenuSearch}
            showMenuSearch={showMenuSearch}
            translate={translate}
            viewportTopOffset={viewportTopOffset}
          />
        </article>

        <section className={basketStyles}>
          <ProfilePanel
            params={{
              id: id?.toString(),
              type,
              vrid: vrId.toString(),
            }}
          />

          <Hidden lgDown>{renderOrderBasket()}</Hidden>
        </section>
      </Page>

      <Hidden lgUp>{checkoutButton}</Hidden>

      {customer.status === 'success' && !!customer.data.EmailAddress && (
        <CampaignConsentDialog
          countryCode={appConfig.CountryCode}
          customer={customer}
        />
      )}

      {showMenuDetailDialog && (
        <MenuDetailDialog
          currency={currency}
          isNutritionInfoError={isNutritionInfoError}
          item={selectedMenuItem}
          languageCode={languageCode}
          menuItemNutritionInfo={menuItemNutritionInfo}
          menuItemOptionSetItemNutritionInfos={
            menuItemOptionSetItemNutritionInfos
          }
          onAddItem={handleAddItemToBasket}
          restaurantPhoneNumber={restaurantDetailData.PhoneNumber}
          setShowDialog={setShowMenuDetailDialog}
          showDialog={showMenuDetailDialog}
          showNutritionInfo={showNutritionInfo}
          translate={translate}
        />
      )}

      {showMenuOptionsDialog && (
        <MenuOptionsDialog
          currency={currency}
          isNutritionInfoError={isNutritionInfoError}
          item={selectedMenuItem}
          languageCode={languageCode}
          menuItemNutritionInfo={menuItemNutritionInfo}
          menuItemOptionSetItemNutritionInfos={
            menuItemOptionSetItemNutritionInfos
          }
          onAddItem={handleAddItemToBasket}
          restaurantPhoneNumber={restaurantDetailData.PhoneNumber}
          setShowDialog={setShowMenuOptionsDialog}
          showDialog={showMenuOptionsDialog}
          showNutritionInfo={showNutritionInfo}
          translate={translate}
        />
      )}

      {showCrossSellDialog && (
        <CrossSellDialog
          basketItems={basketItems}
          currency={currency}
          languageCode={languageCode}
          localisedTotal={localisedBasketTotal}
          menuItems={crossSellMenuItemsData}
          onAddItem={handleAddItemToBasket}
          onCheckout={navigateToCheckout}
          onClickItem={handleClickItem}
          onCloseDialog={(): void => setShowCrossSellDialog(false)}
          onRemoveItem={handleRemoveItemFromBasket}
          translate={translate}
        />
      )}
    </>
  );
}
